import React, { useEffect, useState } from 'react';
import { db } from '../../api/db';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiImage, FiLoader, FiUpload } = FiIcons;

const MediaPicker = ({ onSelect, onClose }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const data = await db.getMedia();
      setMedia(data);
    } catch (e) {
      setError('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const uploaded = await db.uploadMedia(file);
      if (uploaded?.url) {
        onSelect(uploaded.url);
      } else {
        await loadMedia();
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const selectItem = (item) => {
    onSelect(item.url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Select an image</h3>
            <p className="text-sm text-gray-500">Choose an image from the media library.</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-colors hover:opacity-90 disabled:opacity-70"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                <SafeIcon icon={uploading ? FiLoader : FiUpload} className={uploading ? 'animate-spin' : ''} />
                {uploading ? 'Uploading...' : 'Upload new'}
              </span>
            </label>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading media...</div>
          ) : media.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
              <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No images in the media library yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {media.map(item => (
                <button
                  key={item.id}
                  onClick={() => selectItem(item)}
                  className="group text-left bg-white rounded-xl border border-gray-200 overflow-hidden hover:ring-2 transition-all"
                  style={{ '--tw-ring-color': 'var(--brand-primary)' }}
                >
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={item.thumb_url || item.url}
                      alt={item.title || item.filename}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={e => { e.currentTarget.src = item.url; }}
                    />
                  </div>
                  <p className="p-2 text-xs text-gray-500 truncate" title={item.filename || item.title}>
                    {item.filename || item.title || 'Untitled'}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
